import { ClientType } from '@weapp/ebdcoms';
import { CorsComponent, Layout, utils } from '@weapp/ui';
import { eventEmitter, qs } from '@weapp/utils';
import isEqual from 'lodash-es/isEqual';
import React, { ComponentType, PureComponent } from 'react';
import { When } from 'react-if';
import {
  FixedMenu, LayoutType, MultiFill, pageViewClsPrefix,
} from '../../constants';
import CodePageView from '../../pages/CodePageView';
import { getFixedMenuAppId } from '../../routes/page/utils';
import { setTimeoutOnce } from '../../utils';
import ebdcoms from '../../utils/ebdcoms';
import { getApp, getCompName, getCorsProps } from '../../utils/page';
import './grid-layout/index.less';
import './index.less';
import { RightLayoutInfo, RightLayoutProps } from './types';
import UniqueManager from '../../routes/dialogs/uniqueDialog/manager';

const manager = new UniqueManager();

interface UrlParams {
  id: string;
  authType: string;
  analyseReportId?: string;
}

interface RightLayoutStates {
  linkPath: string;
  /** 是否渲染右侧推出 */
  rightLayoutvisible: boolean;
  urlParams: UrlParams;
  linkInfo?: any;
  /** 未渲染右侧推出部分时，内容区域的宽度 */
  originContentWidth: number;
  /** 是否显示右侧推出 */
  isShowRightLayout: boolean;
}

const isDialogInner = (classList: any) => classList?.contains?.('ui-trigger-popupInner')
    || classList?.contains?.('rc-cascader-dropdown')
    || classList?.contains?.('ui-cascader-menus');

class RightLayout extends PureComponent<RightLayoutProps, RightLayoutStates> {
  showRight = false;

  isBlankClose = false;

  linkLayoutKey = '';

  uniqueId = manager.initUniqueId()

  /** 是否空白处可以关闭右侧推出 */

  constructor(props: RightLayoutProps) {
    super(props);

    this.state = {
      linkPath: '',
      rightLayoutvisible: false,
      urlParams: { id: '', authType: '' },
      linkInfo: undefined,
      originContentWidth: 0,
      isShowRightLayout: true, // 右侧推出默认展开
    };

    manager.addUniqueId(this.uniqueId, () => {}, props.type === 'THEME_PAGE');
  }

  componentDidMount() {
    const { store } = this.props;

    eventEmitter.on('@weapp/ebdcoms', 'jumplink.rightLayout', this.handleRightLayout);

    /** 绑定关闭弹窗事件 - 可通过ecode源码单独触发关闭弹窗 */
    eventEmitter.on('@weapp/ebdpage', 'rightlayout.close', this.onCloseRightLayout);

    window.addEventListener('click', this.onListenBlankClick as any);

    store?.events.on('contentView.resize', this.setOriginContentWidth);
  }

  getSnapshotBeforeUpdate(preProps: RightLayoutProps) {
    const { contentDom: preContentDom } = preProps;
    const { contentDom } = this.props;
    const shouldRender = !isEqual(contentDom, preContentDom);

    if (shouldRender) {
      this.setOriginContentWidth();
    }
  }

  componentWillUnmount() {
    const { store } = this.props;

    window.removeEventListener('click', this.onListenBlankClick as any);

    store?.events.off('contentView.resize', this.setOriginContentWidth);
    eventEmitter.off('@weapp/ebdcoms', 'jumplink.rightLayout', this.handleRightLayout);
    eventEmitter.off('@weapp/ebdpage', 'rightlayout.close', this.onCloseRightLayout);
    manager.removeUniqueId(this.uniqueId);
  }

  onCloseRightLayout = () => {
    this.onVisibleChange(false);
  };

  /** 点击空白处可以关闭右侧弹窗 */
  onListenBlankClick = (e: MouseEvent) => {
    if (!this.isBlankClose || this.props.noBlankClose || !this.state.isShowRightLayout) return;
    let target = e.target as HTMLElement;
    let isRightLayout = false;
    let isPopupInner = false;
    do {
      isRightLayout = target.classList.contains(`${pageViewClsPrefix}-right-layout`);
      isPopupInner = isDialogInner(target.classList);
      target = target.parentElement as HTMLElement;
    } while (!isRightLayout && !isPopupInner && target?.tagName !== 'BODY');
    if (this.showRight) {
      this.showRight = false;
      if (!isRightLayout && !isPopupInner && !this.showRight && this.state.isShowRightLayout) {
        this.onVisibleChange(false);
      }

      return;
    }
    if (!isRightLayout && !isPopupInner && !this.showRight && this.state.isShowRightLayout) {
      this.onVisibleChange(false);
    }
  };

  handleRightLayout = (link: string, linkInfo: any) => {
    const { PageType } = ebdcoms.get();
    const [linkPath, queryParams] = link?.split('?');
    const { instanceId } = linkInfo;

    if (instanceId && instanceId !== this.props.store?.instanceId) return null;
    if (!instanceId && this.uniqueId !== manager.activeIndex) return null;

    this.showRight = true;
    this.isBlankClose = linkInfo?.isBlankClose !== false;
    if (linkInfo?.page?.pageType === PageType.Link) {
      this.linkLayoutKey = String(Date.now());
    }
    this.setState(
      {
        linkPath: linkInfo?.page.pageType === PageType.Link ? link : linkPath,
        rightLayoutvisible: true,
        isShowRightLayout: true,
        urlParams: qs.parse(queryParams || '') as unknown as UrlParams,
        linkInfo,
      },
      () => {
        setTimeoutOnce(() => {
          // 渲染右侧推出后需重新触发下页面的resize（右侧推出默认展开）
          this.props.store?.events.emit('resize');
          this.props.onVisibleChange?.(true);
          this.props.store?.events?.emit?.('com.resize');
        }, 300);

        this.rightLayoutInfoChange();
      },
    );
  };

  /** 获取内容区域的宽度样式 */
  getPageStyleWidth = (rightLayoutInfo: RightLayoutInfo) => {
    let width = '100%';

    if (utils.isEmpty(rightLayoutInfo)) return width;

    const { visible, isShow, linkInfo } = rightLayoutInfo!;

    if (visible && isShow) {
      width = `calc(100% - ${linkInfo?.winSizeNew || '40%'})`;
    }

    return width;
  };

  /** 动态改变内容区域的样式和类名 */
  rightLayoutInfoChange = () => {
    const { contentDom, layoutType } = this.props;

    if (!contentDom) return;

    const {
      linkPath, rightLayoutvisible, linkInfo, isShowRightLayout,
    } = this.state;

    const hasRightLayout = !utils.isEmpty(linkPath) && rightLayoutvisible;

    /** 右侧边栏配置信息 */
    const rightLayoutInfo = {
      visible: hasRightLayout,
      isShow: isShowRightLayout,
      linkInfo,
    };

    // 渲染右侧推出时，需要追加inline-block样式，在不添加外层dom情况下使两个div水平布局
    if (rightLayoutInfo.visible) {
      const layoutPrefix = layoutType === LayoutType.FlowLayout ? 'fl' : 'gl';
      const rightLayoutCls = `${pageViewClsPrefix}-${layoutPrefix}-has-right-layout`;

      if (!contentDom.className.includes(rightLayoutCls)) {
        contentDom.className += ` ${pageViewClsPrefix}-${layoutPrefix}-has-right-layout`;
      }
    }

    contentDom.style.width = this.getPageStyleWidth(rightLayoutInfo);
    contentDom.style.transition = 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)';
  };

  getPageInfo() {
    const { linkInfo, linkPath, urlParams } = this.state;
    const { pageType, pageId: id, appid: appId } = linkInfo?.page;
    const { PageType, getEbParams } = ebdcoms.get();
    let appid = '';
    let _pageId = id;
    let key = id;
    let isUnitFill = '';

    // appid 从pageId参数里面取, 流程pageId 路由参数格式 ${appid}_${pageId}
    if (pageType.toUpperCase() === PageType.Workflow) {
      [appid, _pageId, isUnitFill] = _pageId?.split('_');
    }

    // 组合页面appid赋值
    if (pageType.toUpperCase() === PageType.Combinate.toUpperCase()) {
      appid = appId;
    }

    if (_pageId?.includes('-')) {
      [_pageId, key] = _pageId?.split('-');
    }

    if (
      pageType.toUpperCase() === FixedMenu.Base
      || pageType.toUpperCase() === MultiFill.MultireportMonitor
      || pageType.toUpperCase() === MultiFill.MultireportTask
    ) {
      appid = getFixedMenuAppId(pageType, linkPath);
      key += pageType;
    }

    // urlParams: 根据linkPath去获取参数
    const pageParams = getEbParams('pageParams', _pageId || '');
    const pageUrlParmas = pageParams?.urlParams || {};

    const _urlParams = {
      ...pageUrlParmas,
      ...urlParams,
    };

    return {
      appid,
      key: `${key}_${JSON.stringify(_urlParams)}`,
      pageId: _pageId,
      type: pageType,
      urlParams: _urlParams,
      isUnitFill: isUnitFill === '1',
    };
  }

  /** 获取原始页面宽度，即总宽度 */
  setOriginContentWidth = () => {
    const {
      linkPath, rightLayoutvisible, isShowRightLayout, linkInfo,
    } = this.state;
    const { contentDom } = this.props;

    const hasRightLayout = !utils.isEmpty(linkPath) && rightLayoutvisible;

    // 右侧分栏显示时，通过当前内容区域的宽度和winSizeNew计算出总宽度
    if (hasRightLayout) {
      const currContentWidth = contentDom?.offsetWidth || 0;
      const { winSizeNew = '40%' } = linkInfo;
      const [winSizeNumber = 0] = winSizeNew.split('%');

      const warpperWidth = currContentWidth / ((100 - winSizeNumber) / 100);

      this.setState({
        originContentWidth: warpperWidth,
      });
    } else if (!hasRightLayout || !isShowRightLayout) {
      // 未渲染右侧分栏时或者右侧分栏隐藏时
      this.setState({
        originContentWidth: contentDom?.offsetWidth || 0,
      });
    }
  };

  /** 根据右侧配置获取实际宽度px */
  getRightLayoutWidth = () => {
    const {
      linkPath, rightLayoutvisible, linkInfo, originContentWidth, isShowRightLayout,
    } = this.state;

    const hasRightLayout = !utils.isEmpty(linkPath) && rightLayoutvisible;

    if (hasRightLayout && isShowRightLayout && linkInfo) {
      const { winSizeNew = '40%' } = linkInfo;

      if (winSizeNew.includes('%')) {
        const [winSizeNumber = 0] = winSizeNew.split('%');

        return originContentWidth * (winSizeNumber / 100);
      }

      const [winSizeNumber = 0] = winSizeNew.split('px');

      // 避免打开的页面存在滚动条时撑大宽度导致换行显示
      return winSizeNumber;
    }

    return 0;
  };

  onVisibleChange = (visible: boolean) => {
    this.setState({ isShowRightLayout: visible }, () => {
      // 等待折叠动画执行完成后，重新排列布局
      setTimeoutOnce(() => {
        this.props.store?.events.emit('resize');
        this.props.onVisibleChange?.(visible);
        this.props.store?.events?.emit?.('com.resize');
      }, 300);

      this.rightLayoutInfoChange();
    });
  };

  /** 渲染右侧推出链接内容 */
  renderComponent = () => {
    const { linkPath, rightLayoutvisible, linkInfo } = this.state;

    const hasRightLayout = !utils.isEmpty(linkPath) && rightLayoutvisible;

    if (!hasRightLayout) return null;

    const { PageType } = ebdcoms.get();

    if (linkInfo?.page?.pageType === PageType.Link) {
      return this.renderLink();
    }

    if (linkInfo?.page?.pageType === 'CUSTOM') {
      return this.renderCustomPage();
    }

    if (linkInfo?.page?.pageType === 'codePage') {
      return (
        <CodePageView weId={`${this.props.weId || ''}_jh1q30`} ecodeId={linkInfo?.page?.ecodeId} />
      );
    }

    return this.renderPage();
  };

  /** 渲染模块自定义页面 */
  renderCustomPage = () => {
    const { linkInfo } = this.state;
    const {
      page: { customParams = {} },
    } = linkInfo;

    return <CorsComponent weId={`${this.props.weId || ''}_ah4987`} {...customParams} />;
  };

  /** 渲染自定义链接iframe页面 */
  renderLink = () => {
    const { linkPath } = this.state;
    let iframeSrc = '';

    const { getDomainName } = ebdcoms.get();

    // 针对/sp/ebdapp/view/xxx等内部链接，需拼接域名
    if (
      !(linkPath.startsWith('https://') || linkPath.startsWith('http://'))
      && linkPath.startsWith('/')
    ) {
      iframeSrc = `${getDomainName()}${linkPath}`;
    } else {
      iframeSrc = linkPath;
    }

    return (
      <iframe
        width="100%"
        height="100%"
        frameBorder={0}
        style={{
          display: 'block',
        }}
        key={this.linkLayoutKey}
        title="link"
        src={iframeSrc}
      />
    );
  };

  /** 渲染EB页面、表单表格等页面 */
  renderPage = () => {
    const { linkPath, linkInfo } = this.state;
    const { runType, isPreview = false, servicePath } = this.props;

    const client = 'PC'; // client：不展示移动端页面，故为PC
    const {
      appid, pageId, type, key, urlParams, isUnitFill,
    } = this.getPageInfo();
    let app = getApp(type, pageId, linkPath, appid);
    let compName = getCompName(type, pageId, client, linkPath) || '';
    const { page } = linkInfo;
    const { PageType } = ebdcoms.get();

    if (page?.pageType === PageType.BussComp) {
      app = page?.module || '';
      compName = page?.compName || '';
    }

    if (!app || !compName) {
      return null;
    }

    const corsParams = {
      type,
      pageId,
      appId: appid,
      getRightButton: undefined, // TODO: 表单页面需确认PageView是否能接收到customRenderRight的props
      mobileView: false, // mobileView: 不展示移动端页面，故为false
      urlParams,
      showGroup: false, // showGroup：应用菜单运行页是否显示分组， 页面处固定不显示
      menuInfo: undefined, // menuInfo: 应用菜单的运行页才会存在，页面处固定为undefined
      clientType: client as ClientType,
      pathname: linkPath, // pathname：右侧推出的信息通过comEvent传递，故采用linkPath
      isUnitFill,
    };

    const corsProps = getCorsProps(corsParams);

    return (
      <CorsComponent
        weId={`${this.props.weId || ''}_ah4987`}
        app={app}
        key={key}
        compName={compName}
        mount={this.props.mount}
        runType={runType}
        isPreview={isPreview}
        servicePath={servicePath}
        {...corsProps}
        closeType="rightlayout.close" /** 传递给组件告诉关闭方式 */
      />
    );
  };

  render() {
    const { linkPath, rightLayoutvisible, isShowRightLayout } = this.state;

    const hasRightLayout = !utils.isEmpty(linkPath) && rightLayoutvisible;

    const width = this.getRightLayoutWidth();

    return (
      <When weId={`${this.props.weId || ''}_nids1d`} condition={hasRightLayout}>
        <aside
          className={`${pageViewClsPrefix}-right-layout`}
          style={{
            width: `${Number(width)}px`,
          }}
        >
          <Layout.Box
            weId={`${this.props.weId || ''}_ogs89v`}
            type="side"
            right
            allowHidden
            hidden={!isShowRightLayout}
            hiddenControlled
            onVisibleChange={this.onVisibleChange}
          >
            {this.renderComponent()}
          </Layout.Box>
        </aside>
      </When>
    );
  }
}

export default RightLayout as unknown as ComponentType<RightLayoutProps>;
